// @flow

// Ideal link with different ratios to test http://localhost:3000/en/listing?id=QWdncmVnYXRlc0xpc3Rpbmc6MTc4Nzc1OA%3D%3D

import * as React from 'react';
import { Box, Flex, useSystem } from 'react-system';
import { IconButton } from '@material-ui/core';
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';
import scrollbarSize from 'dom-helpers/scrollbarSize';
import { Carousel } from '@realadvisor/carousel';
import { cloudinaryUrl } from '@realadvisor/cloudinary';
import { Image } from '@realadvisor/image';
import { getOffsetTop } from '../utils/getOffsetTop';
import { useWindowSize } from '../hooks/measure';
import { useIsMobile } from '../hooks/mobile';
import { useTheme } from '../theme';
import * as icons from '../icons';
import { useLog } from '../hooks/log';
import { Center } from './Center';

type GalleryImage = {
  +bucketName: string,
  +fileName: string,
  +title?: null | string,
  ...
};

const aspectRatio = ratio => ({
  flex: 1,
  position: 'relative',

  '&:before': {
    display: 'block',
    width: '100%',
    paddingTop: `${100 * ratio}%`,
    content: "''",
  },
});

const useImageRatios = (
  images: $ReadOnlyArray<GalleryImage>,
): $ReadOnlyArray<number> => {
  const [ratios, setRatios] = React.useState([]);
  const ratiosRef = React.useRef(images.map(() => null));
  const maxLoadedFilledLength = React.useRef(0);
  const logger = useLog();

  React.useEffect(() => {
    const setValueAt = (val, idx) => {
      ratiosRef.current[idx] = val;
      let filledIdx = 0;

      while (
        ratiosRef.current[filledIdx] != null &&
        filledIdx < ratiosRef.current.length
      ) {
        filledIdx += 1;
      }

      // we update only if new filled interval from 0...filledIdx occured
      // we are not interested in images loaded at the end as we can't render them
      if (filledIdx > maxLoadedFilledLength.current) {
        maxLoadedFilledLength.current = filledIdx;
        setRatios(
          [...ratiosRef.current]
            .slice(0, maxLoadedFilledLength.current)
            .map(v => v ?? 0),
        );
      }
    };

    images.forEach((image, index) => {
      const img = new window.Image();

      img.onload = () => {
        setValueAt(img.height / img.width, index);
      };
      img.onerror = () => {
        logger.error(new Error('image load error'), img.src);
        setValueAt(1 / 4, index);
      };

      img.src = cloudinaryUrl(
        { w: 100, f: 'jpg' },
        { bucketName: image.bucketName, fileName: image.fileName },
      );
    });
  }, [images, logger]);

  return ratios;
};

type GalleryProps = {|
  images: $ReadOnlyArray<GalleryImage>,
  startAt: number,
  altPrefix?: string,
|};

const regex = /(auto|scroll)/;

const style = (node, prop) => getComputedStyle(node).getPropertyValue(prop);

const scroll = node =>
  regex.test(
    style(node, 'overflow') +
      style(node, 'overflow-y') +
      style(node, 'overflow-x'),
  );

const getScrollParent = (node: HTMLElement | null) =>
  node == null || node === document.body
    ? null
    : scroll(node)
    ? node
    : node.parentNode instanceof HTMLElement
    ? getScrollParent(node.parentNode)
    : null;

const GalleryMobile = (props: GalleryProps) => {
  const { text, colors } = useTheme();
  const { media } = useSystem();
  const ratios = useImageRatios(props.images);
  const canScrollOnceRef = React.useRef(true);

  const handleScrollToRef = React.useCallback(ref => {
    if (ref != null && canScrollOnceRef.current) {
      const nodeScroll = getScrollParent(ref);
      if (nodeScroll != null) {
        // Give some space above image
        const TOP_OFFSET = 16;
        nodeScroll.scrollTop = getOffsetTop(ref) - TOP_OFFSET;
      }
      canScrollOnceRef.current = false;
    }
  }, []);

  return (
    <Box>
      {ratios.map((imageRatio, index) => {
        const image = props.images[index];
        return (
          <Box key={index} mb={[2, 3]}>
            <Box
              px={4}
              css={[
                aspectRatio(imageRatio),
                {
                  backgroundColor: `rgba(255, 255, 255, 0.1)`,
                  position: 'relative',
                },
              ]}
              mb={image.title == null ? 4 : 0}
            >
              <div
                ref={
                  props.startAt > 0 && index === props.startAt
                    ? handleScrollToRef
                    : null
                }
                css={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  right: 0,
                  bottom: 0,
                }}
              >
                <Image
                  // This property is only for type safety
                  src={{
                    bucketName: image.bucketName,
                    fileName: image.fileName,
                  }}
                  srcSize={[
                    [375, 375 * imageRatio],
                    [960, 960 * imageRatio],
                    [1200, 1200 * imageRatio],
                  ]}
                  loading="lazy"
                  alt={`${
                    props.altPrefix != null ? `${props.altPrefix}.` : ''
                  } Photo ${index + 1}.`}
                />
              </div>
            </Box>
            {image.title != null && (
              <Box
                px={3}
                py={2}
                css={[
                  media([text.subtitle2, text.subtitle1]),
                  {
                    color: colors.common.white,
                    textAlign: 'center',
                  },
                ]}
              >
                {image.title}
              </Box>
            )}
          </Box>
        );
      })}
      {ratios.length < props.images.length && <Box height={'1000px'} />}
    </Box>
  );
};

const Position = ({ value, total }) => {
  return (
    <Flex
      alignItems="center"
      mb={1}
      ml={'20px'}
      css={{
        position: 'absolute',
        left: 0,
        bottom: 0,
        color: 'white',
      }}
    >
      <icons.PhotoCamera size={24} />
      <Box
        ml={1}
        css={{
          position: 'relative',
          top: '-2px',
          whiteSpace: 'nowrap',
          textShadow: '1px 1px 1px rgba(0,0,0,0.5)',
        }}
      >
        {value} of {total}
      </Box>
    </Flex>
  );
};

const GalleryDesktop = (props: GalleryProps) => {
  const windowSize = useWindowSize();
  const ratio = windowSize.innerHeight / (windowSize.innerWidth || 1);

  // TODO: startAt and bottom counter
  // props.images[(index + props.startAt) % props.images.length];

  return (
    <Box my="auto">
      <Carousel
        media={{
          itemsOnScreen: 1,
          aspectRatio: ratio,
          gap: 0,
        }}
        buttonsVisibility={'always'}
        Position={Position}
        initialOffset={props.startAt}
        autoFocus={true}
      >
        {props.images.map((image, index) => (
          <Image
            key={index}
            css={{
              padding: '8px 0 16px 0',
            }}
            src={{
              bucketName: image.bucketName,
              fileName: image.fileName,
            }}
            options={{ c: 'limit' }}
            objectFit="contain"
            srcSize={[
              [360, 360],
              [1280, 1280],
              [1920, 1920],
            ]}
            alt={`${
              props.altPrefix != null ? `${props.altPrefix}.` : ''
            } Photo ${index + 1}.`}
          />
        ))}
      </Carousel>
    </Box>
  );
};

type Props = {|
  onClose: () => void,
  images: $ReadOnlyArray<GalleryImage>,
  startAt: number,
  altPrefix?: string,
|};

export const Gallery = (props: Props): React.Node => {
  const isMobile = useIsMobile();
  const { colors } = useTheme();
  const bgRef = React.useRef<React.ElementRef<'div'> | null>(null);
  const propsRef = React.useRef(props);

  React.useEffect(() => {
    const elt = bgRef.current;
    if (elt) {
      disableBodyScroll(elt, { reserveScrollBarGap: true });
      return () => {
        enableBodyScroll(elt);
      };
    }
  }, []);

  // onDestroyed can be called after timeout, etc
  React.useLayoutEffect(() => {
    propsRef.current = props;
  });

  React.useEffect(() => {
    const handleKeyDown = (evt: KeyboardEvent) => {
      if (evt.code === 'Escape') {
        propsRef.current.onClose();
        evt.stopPropagation();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  const windowSize = useWindowSize();
  const scrollSize = scrollbarSize();

  return (
    <div
      ref={bgRef}
      // style={style}
      css={{
        position: 'fixed',
        left: 0,
        width: '100vw',
        top: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.84)',
        zIndex: 1300,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div
        css={{
          width: windowSize.innerWidth - scrollSize,
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isMobile == null ? null : isMobile ? (
          <Center
            css={{
              padding: '0 !important',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Flex
              onClick={() => {
                props.onClose();
              }}
              pt={[3, 0]}
              css={[
                {
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                },
              ]}
            >
              <IconButton
                css={{
                  position: 'absolute',
                  top: '24px',
                  right: '24px',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    pointer: 'cursor',
                  },
                }}
              >
                <icons.Close fill={colors.common.white} size={24} />
              </IconButton>
            </Flex>
            <GalleryMobile
              startAt={props.startAt}
              images={props.images}
              altPrefix={props.altPrefix}
            />
          </Center>
        ) : (
          <>
            <IconButton
              onClick={() => {
                props.onClose();
              }}
              css={{
                position: 'absolute',
                zIndex: 10,
                top: '22px',
                right: '22px',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  pointer: 'cursor',
                },
              }}
            >
              <icons.Close fill={colors.common.white} size={24} />
            </IconButton>

            <GalleryDesktop
              startAt={props.startAt}
              images={props.images}
              altPrefix={props.altPrefix}
            />
          </>
        )}
      </div>
    </div>
  );
};
